import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { CopyButton } from "@/components/Button/CopyButton";
import { InfoBox } from "@/components/InfoBox";
import { MotifIcon } from "@/components/MotifIcon";
import { Tooltip } from "@/components/Tooltip";
import { Typography } from "@/components/Typography";
import { copyPlainText, copyRichText } from "@/features/clipboard/copyRichText";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { buildItDnsEmail } from "@/pages/channels/pages/emailOnboard/utils/buildItDnsEmail";

// ── Types ──

type DnsRecordStatus = "pending" | "verified" | "failed";

interface DnsRecord {
  type: "CNAME" | "TXT";
  host: string;
  value: string;
  status: DnsRecordStatus;
}

// ── Styles (matching demo styles.css) ──

const styles = {
  // dns-shell
  shell: {
    position: "relative" as const,
    border: `1px solid ${theme.colors.staticFgLine}`,
    borderRadius: "12px",
    padding: "24px 24px 16px",
    backgroundColor: theme.colors.white,
  },
  // verify success overlay — sits on top of the records table to
  // celebrate the just-verified state. Session-local: dismissed by
  // either button and not restored on remount.
  verifyOverlay: {
    position: "absolute" as const,
    inset: 0,
    borderRadius: "12px",
    backgroundColor: "rgba(255, 255, 255, 0.96)",
    backdropFilter: "blur(2px)",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "24px",
    textAlign: "center" as const,
    zIndex: 2,
  },
  verifyOverlayActions: {
    display: "flex",
    flexDirection: "row" as const,
    gap: "10px",
    marginTop: "16px",
  },
  // dns-domain (big title)
  domainTitle: {
    fontSize: "22px",
    fontWeight: 700,
    lineHeight: 1.2,
    color: theme.colors.staticFgTitle,
  },
  // dns-install-table
  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
  },
  th: {
    borderTop: `1px solid ${theme.colors.staticFgLine}`,
    padding: "14px 10px",
    fontSize: "12px",
    fontWeight: 700,
    color: theme.colors.staticFgNote,
    textAlign: "left" as const,
    verticalAlign: "middle" as const,
  },
  td: {
    borderTop: `1px solid ${theme.colors.staticFgLine}`,
    padding: "14px 10px",
    fontSize: "13px",
    textAlign: "left" as const,
    verticalAlign: "middle" as const,
    color: theme.colors.staticFgTitle,
  },
  // dns-install-status
  statusPending: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontWeight: 600,
    fontSize: "13px",
    color: theme.colors.staticFgPlaceholder,
  },
  statusVerified: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontWeight: 600,
    fontSize: "13px",
    color: theme.colors.staticFgPositive,
  },
  statusFailed: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontWeight: 600,
    fontSize: "13px",
    color: theme.colors.staticFgNegative,
  },
  // mono text for HOST/VALUE cells
  mono: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: "13px",
    wordBreak: "break-all" as const,
  },
  // Read-only rendering of the message handed to IT. Monospace and
  // pre-wrapped so the record values line up the way they will after paste.
  emailPreview: {
    margin: 0,
    padding: "12px 14px",
    maxHeight: "260px",
    overflowY: "auto",
    border: `1px solid ${theme.colors.staticFgLine}`,
    borderRadius: "8px",
    backgroundColor: theme.colors.staticBgDisable,
    color: theme.colors.staticFgTitle,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: "12px",
    lineHeight: 1.55,
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-all" as const,
  },
  // verify overlay check icon — green circle background, straight SVG
  // checkmark inside (MotifIcon "check"). Previously rendered as a
  // text ✓ glyph which had a noticeably curvy stroke.
  modalIcon: {
    width: 56,
    height: 56,
    marginBottom: "8px",
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    backgroundColor: theme.colors.colorGreen10,
    color: theme.colors.colorGreen60,
    fontSize: "32px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

// ── Helpers ──

/**
 * Build placeholder DNS records from domain info.
 * In production these come from the API response after POST /applications.
 */
function buildDnsRecords(
  effectiveDomain: string,
  brandDomain: string,
  subuserId: string,
): Array<DnsRecord> {
  return [
    {
      type: "CNAME",
      host: effectiveDomain,
      value: `u${subuserId}.wl144.sendgrid.net`,
      status: "pending",
    },
    {
      type: "CNAME",
      host: `s1._domainkey.${brandDomain}`,
      value: `s1.domainkey.u${subuserId}.wl144.sendgrid.net`,
      status: "pending",
    },
    {
      type: "CNAME",
      host: `s2._domainkey.${brandDomain}`,
      value: `s2.domainkey.u${subuserId}.wl144.sendgrid.net`,
      status: "pending",
    },
    {
      type: "TXT",
      host: `_dmarc.${brandDomain}`,
      value: "v=DMARC1; p=none",
      status: "pending",
    },
  ];
}

// ── Component ──

/**
 * Tab-level state passed from the page. Constructed from the new
 * Channel + SenderDomain entities — see EmailOnboardPage.
 *
 * - `id` is the sender domain id (the path param for validate).
 * - `channelId` is the parent channel id.
 * - `dnsRecords` follow the new shape with `name`, `type`, `host`,
 *   `data`, and `valid` fields.
 * - `brandDomain` is FE-only and may be absent for channels onboarded
 *   prior to this view tracking it.
 */
/**
 * Per-record DNS validation results. Typed structurally so both the validate
 * response and the page-provided application data satisfy it.
 */
type DnsValidationStatuses = Pick<
  InterludeTypes["SenderDomain"],
  "mailCnameStatus" | "dkim1Status" | "dkim2Status" | "dmarcStatus"
>;

// The validate endpoint records each DNS record's result in the per-record
// *Status fields, keyed by the record's canonical name ("mail_cname" / "dkim1"
// / "dkim2" / "dmarc") — NOT in dnsRecords[].valid, which keeps its
// create-time value and is never updated by validation. Resolve a record's
// display status from its name.
//
// `failAs` controls how a "fail" reads: before an explicit verify it stays
// "pending" (a freshly created domain defaults every *Status to "fail", so we
// must not show alarming red rows up front); right after a verify we surface
// it as "failed".
const resolveRecordStatus = (
  recordName: string,
  statuses: DnsValidationStatuses,
  failAs: DnsRecordStatus = "pending",
): DnsRecordStatus => {
  const statusByName: Record<string, "pass" | "fail"> = {
    mail_cname: statuses.mailCnameStatus,
    dkim1: statuses.dkim1Status,
    dkim2: statuses.dkim2Status,
    dmarc: statuses.dmarcStatus,
  };
  const status = statusByName[recordName];
  if (status === "pass") return "verified";
  return status === "fail" ? failAs : "pending";
};

type ApplicationData = {
  id: number;
  channelId: number;
  brandDomain?: string;
  senderDomain: string;
  dnsRecords: InterludeTypes["SenderDomain"]["dnsRecords"];
  status: "pending_dns" | "active";
} & DnsValidationStatuses;

interface DnsVerifyTabProps {
  onJumpToTab: (tab: "email-info") => void;
  dnsProvider?: string | undefined;
  application?: ApplicationData | null | undefined;
}

export const DnsVerifyTab: React.FC<DnsVerifyTabProps> = function DnsVerifyTab({
  onJumpToTab,
  dnsProvider,
  application,
}) {
  const { t } = useTranslation();
  // Use application data if available, otherwise fall back to placeholders
  const brandDomain = application?.brandDomain ?? "cresclab.site";
  const effectiveDomain =
    application?.senderDomain ?? "marketing.cresclab.site";

  const records = useMemo(() => {
    // Use DNS records from API if available
    if (application?.dnsRecords && application.dnsRecords.length > 0) {
      return application.dnsRecords.map((r) => ({
        type: r.type.toUpperCase() as "CNAME" | "TXT",
        host: r.host,
        value: r.data,
        status: resolveRecordStatus(r.name, application),
      }));
    }
    // Placeholder records when the backend response is not yet available.
    // In normal flow `dnsRecords` is populated right after channel creation.
    return buildDnsRecords(effectiveDomain, brandDomain, "0");
  }, [application, effectiveDomain, brandDomain]);

  // Store verify result overlay — null means use records from props
  const [verifyResult, setVerifyResult] = useState<{
    statuses: Array<DnsRecordStatus>;
    reasons: Array<string | null>;
  } | null>(null);

  // Derive display statuses/reasons: verify result takes priority, else from records
  const recordStatuses = useMemo(
    () => verifyResult?.statuses ?? records.map((r) => r.status),
    [verifyResult, records],
  );
  const recordReasons = useMemo(
    () => verifyResult?.reasons ?? records.map(() => null),
    [verifyResult, records],
  );

  const [isVerifying, setIsVerifying] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const isCloudflare =
    dnsProvider?.toLowerCase() === "cloudflare" || !dnsProvider;

  const allVerified = recordStatuses.every((s) => s === "verified");

  const { enqueueSnackbar } = useSnackbar();

  // Rebuilt from `records` on every render, so what IT receives always matches
  // what the backend issued. `{{domain}}` is interpolated by i18next itself --
  // passing the value through `t` options is the only way it survives, since
  // i18next strips any placeholder it has no option for.
  const itEmailContent = useMemo(() => {
    const domainOption = { domain: effectiveDomain };
    return buildItDnsEmail({
      records: records.map((record) => ({
        type: record.type,
        host: record.host,
        value: record.value,
      })),
      copy: {
        intro: t(
          "emailOnboard.dnsVerify.copyForIt.email.intro",
          "Hi，\n\n我是 {您的姓名}，因公司開通數位行銷工具 MAAC 的 Email 渠道功能，需請您協助在網域 {{domain}} 新增以下 DNS 記錄。",
          domainOption,
        ),
        domainLabel: t(
          "emailOnboard.dnsVerify.copyForIt.email.domainLabel",
          "要設定的網域：{{domain}}",
          domainOption,
        ),
        columnType: t(
          "emailOnboard.dnsVerify.copyForIt.email.columnType",
          "類型",
        ),
        columnHost: t(
          "emailOnboard.dnsVerify.copyForIt.email.columnHost",
          "主機名稱 (Host)",
        ),
        columnValue: t(
          "emailOnboard.dnsVerify.copyForIt.email.columnValue",
          "值 (Value)",
        ),
        notesTitle: t(
          "emailOnboard.dnsVerify.copyForIt.email.notesTitle",
          "設定注意事項：",
        ),
        notes: [
          // Cloudflare proxies records by default, which makes verification
          // fail no matter how correctly IT enters them -- so it leads the
          // list, and only for the providers the page already warns about.
          // Worded for its position after the table ("above"), unlike the
          // on-screen warning that sits before it.
          ...(isCloudflare
            ? [
                t(
                  "emailOnboard.dnsVerify.copyForIt.email.note.cloudflareProxy",
                  "若使用 Cloudflare，請將上述 DNS Record 的 Proxy 設為「僅限 DNS」（灰色雲朵圖示），否則驗證將會失敗",
                ),
              ]
            : []),
          t(
            "emailOnboard.dnsVerify.copyForIt.email.note.exactCopy",
            "主機名稱與值請完整複製，勿加入多餘空白或換行",
          ),
          t(
            "emailOnboard.dnsVerify.copyForIt.email.note.rootDomain",
            "部分 DNS 服務商會自動補上主網域，若欄位後方已顯示 .{{domain}}，主機名稱只需填寫前綴部分",
            domainOption,
          ),
          t(
            "emailOnboard.dnsVerify.copyForIt.email.note.ttl",
            "TTL 使用預設值即可",
          ),
          t(
            "emailOnboard.dnsVerify.copyForIt.email.note.replyBack",
            "設定生效後請回覆我，我會到後台點「驗證 DNS」確認",
          ),
        ],
        closing: t(
          "emailOnboard.dnsVerify.copyForIt.email.closing",
          "謝謝您的協助！",
        ),
      },
    });
  }, [records, effectiveDomain, isCloudflare, t]);

  // Two explicit copy actions rather than one clever one: a rich editor that
  // cannot render tables (Slack flattens ours into a single run-on line) still
  // prefers the html flavor when both are on the clipboard, so the only
  // reliable way to get readable text there is to offer plain text alone.
  const notifyCopyFailed = useHandler(() => {
    enqueueSnackbar(
      t(
        "emailOnboard.dnsVerify.copyForIt.copyFailed",
        "複製失敗，請手動選取內容後複製",
      ),
      { variant: "error" },
    );
  });

  const handleCopyForEmail = useHandler(async () => {
    const flavor = await copyRichText(
      itEmailContent.html,
      itEmailContent.plain,
    );
    if (flavor === "failed") {
      notifyCopyFailed();
      return;
    }
    enqueueSnackbar(
      flavor === "rich"
        ? t(
            "emailOnboard.dnsVerify.copyForIt.copiedRich",
            "已複製，貼到 Email 會保留表格排版",
          )
        : t("emailOnboard.dnsVerify.copyForIt.copiedPlain", "已複製純文字版本"),
      { variant: "success" },
    );
  });

  const handleCopyPlain = useHandler(() => {
    if (!copyPlainText(itEmailContent.plain)) {
      notifyCopyFailed();
      return;
    }
    enqueueSnackbar(
      t("emailOnboard.dnsVerify.copyForIt.copiedPlain", "已複製純文字版本"),
      { variant: "success" },
    );
  });

  // Verify DNS via the unified channel sender-domain endpoint.
  // The backend re-runs ESP validation, updates per-record statuses,
  // and may transition Channel.status from `disconnected` to
  // `building_reputation` when all records pass.
  const verifyMutation = interlude.channel.useValidateSenderDomain(
    {
      params: {
        channelId: application?.channelId ?? 0,
        domainId: application?.id ?? 0,
      },
    },
    {
      onSuccess: (data) => {
        setIsVerifying(false);
        setVerifyResult({
          statuses: data.dnsRecords.map((r) =>
            resolveRecordStatus(r.name, data, "failed"),
          ),
          // Backend does not currently surface per-record failure reasons.
          reasons: data.dnsRecords.map(() => null),
        });
        if (data.status === "verified") {
          setShowSuccessModal(true);
        }
      },
      onError: () => {
        setIsVerifying(false);
      },
    },
  );

  const handleVerify = useHandler(() => {
    if (!application?.channelId || !application?.id) return;
    setIsVerifying(true);
    verifyMutation.mutate(undefined);
  });

  return (
    <>
      <Stack gap="24px">
        {/* Section title + tooltip */}
        <Box>
          <Stack direction="row" alignItems="center" gap="6px">
            <Typography
              variant="h3"
              fontWeight={700}
              color={theme.colors.staticFgTitle}
            >
              {t("emailOnboard.dnsVerify.title", "DNS 驗證")}
            </Typography>
            <Tooltip
              title={t(
                "emailOnboard.dnsVerify.tooltip",
                "這一步是要把系統提供的 DNS 紀錄交給 IT 或工程師設定，讓寄件網域完成驗證。完成後，平台才能確認這個網域可合法用來寄送 Email，降低信件被擋下或進垃圾郵件的風險。",
              )}
            >
              <Box component="span" sx={{ display: "flex" }}>
                <MotifIcon
                  un-i-motif="circle_info"
                  style={{
                    fontSize: 14,
                    color: theme.colors.staticFgNote,
                  }}
                />
              </Box>
            </Tooltip>
          </Stack>
          <InfoBox
            variant="primary"
            sx={{ mt: "12px" }}
            title={t(
              "emailOnboard.dnsVerify.guide.title",
              "完成 DNS 驗證的 3 個步驟",
            )}
            description={
              <Stack gap="4px">
                <Typography variant="body">
                  {t(
                    "emailOnboard.dnsVerify.guide.step1",
                    "1. 將下方 4 筆 DNS 紀錄轉交給您的網域管理人員，通常為公司 IT、網站工程師，或當初購買、維運公司網域的同事。",
                  )}
                </Typography>
                <Typography variant="body">
                  {t(
                    "emailOnboard.dnsVerify.guide.step2",
                    "2. 由對方登入您網域的 DNS 管理平台（如 Cloudflare、GoDaddy、AWS Route 53 等），依序將 4 筆紀錄新增至 DNS 設定。",
                  )}
                </Typography>
                <Typography variant="body">
                  {t(
                    "emailOnboard.dnsVerify.guide.step3",
                    "3. 紀錄新增完成後，請回到本頁面點擊下方「驗證 DNS」按鈕進行驗證；驗證通過後，Email 渠道將自動啟用，您即可開始使用品牌網域寄送 Email。",
                  )}
                </Typography>
              </Stack>
            }
          />
        </Box>

        {/* Cloudflare proxy warning — hide once the domain is verified
            since the records no longer need new DNS edits. */}
        {isCloudflare && application?.status !== "active" ? (
          <InfoBox
            variant="warning"
            description={t(
              "emailOnboard.dnsVerify.cloudflareWarning",
              "若使用 Cloudflare，請將以下 DNS Record 的 Proxy 設為「僅限 DNS」（灰色雲朵圖示），否則驗證將會失敗。",
            )}
          />
        ) : null}

        {/* DNS Record table */}
        <Box sx={styles.shell}>
          <Stack
            direction="row"
            alignItems="center"
            gap="12px"
            sx={{ flexWrap: "wrap" }}
          >
            <Box sx={styles.domainTitle}>{effectiveDomain}</Box>
            <Badge
              variant={
                application?.status === "active" ? "positive" : "attentive"
              }
            >
              {application?.status === "active"
                ? t("emailOnboard.dnsVerify.domain.statusVerified", "已驗證")
                : t("emailOnboard.dnsVerify.domain.statusPending", "未驗證")}
            </Badge>
          </Stack>
          <TableContainer>
            <Table sx={styles.table}>
              <TableHead>
                <TableRow>
                  <TableCell sx={styles.th}>STATUS</TableCell>
                  <TableCell sx={styles.th}>TYPE</TableCell>
                  <TableCell sx={styles.th}>HOST</TableCell>
                  <TableCell sx={styles.th} />
                  <TableCell sx={styles.th}>VALUE</TableCell>
                  <TableCell sx={styles.th} />
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((record, i) => (
                  <TableRow key={i}>
                    <TableCell sx={styles.td}>
                      <Box
                        sx={
                          recordStatuses[i] === "verified"
                            ? styles.statusVerified
                            : recordStatuses[i] === "failed"
                              ? styles.statusFailed
                              : styles.statusPending
                        }
                      >
                        <Box component="span" sx={{ fontSize: "13px" }}>
                          ●
                        </Box>
                        {recordStatuses[i] === "verified"
                          ? "Verified"
                          : recordStatuses[i] === "failed"
                            ? "Failed"
                            : "Pending"}
                      </Box>
                      {recordStatuses[i] === "pending" && recordReasons[i] ? (
                        <Box
                          sx={{
                            mt: "4px",
                            fontSize: "10px",
                            color: theme.colors.staticFgNegative,
                            lineHeight: 1.4,
                          }}
                        >
                          {recordReasons[i]}
                        </Box>
                      ) : null}
                    </TableCell>
                    <TableCell sx={styles.td}>{record.type}</TableCell>
                    <TableCell sx={{ ...styles.td, ...styles.mono }}>
                      {record.host}
                    </TableCell>
                    <TableCell sx={styles.td}>
                      <CopyButton
                        text={record.host}
                        variant="secondary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ ...styles.td, ...styles.mono }}>
                      {record.value}
                    </TableCell>
                    <TableCell sx={styles.td}>
                      <CopyButton
                        text={record.value}
                        variant="secondary"
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {/* In-place overlay over the records table. Two phases share
              the same slot:
                - Verifying: spinner + "正在驗證 DNS" copy. The verify
                  mutation calls SendGrid's domain validation API, which
                  performs real DNS lookups (typically 2-5 s).
                - Just verified: green check + success copy + actions.
              Both are pure session state — dismissed by either button
              click on success, never restored on remount, never shown
              for channels verified in an earlier session. */}
          {isVerifying || showSuccessModal ? (
            <Box sx={styles.verifyOverlay}>
              {isVerifying ? (
                <>
                  <CircularProgress
                    size={48}
                    thickness={4}
                    sx={{ color: theme.colors.colorBlue50 }}
                  />
                  <Typography
                    variant="h2"
                    fontWeight={700}
                    color={theme.colors.staticFgTitle}
                    css={css({ marginTop: 4 })}
                  >
                    {t("emailOnboard.dnsVerify.loading.title", "正在驗證 DNS")}
                  </Typography>
                  <Typography
                    variant="body"
                    color={theme.colors.colorNeutral80}
                    css={css({ maxWidth: 360, lineHeight: 1.7 })}
                  >
                    {t(
                      "emailOnboard.dnsVerify.loading.description",
                      "系統正在檢查 DNS Record 是否已完成設定，請稍候...",
                    )}
                  </Typography>
                </>
              ) : (
                <>
                  <Box sx={styles.modalIcon}>
                    <MotifIcon un-i-motif="check" />
                  </Box>
                  <Typography
                    variant="h2"
                    fontWeight={700}
                    color={theme.colors.staticFgTitle}
                  >
                    {t(
                      "emailOnboard.dnsVerify.success.title",
                      "DNS 驗證已通過",
                    )}
                  </Typography>
                  <Typography
                    variant="body"
                    color={theme.colors.colorNeutral80}
                    css={css({ maxWidth: 360, lineHeight: 1.7 })}
                  >
                    {t(
                      "emailOnboard.dnsVerify.success.description",
                      "您的寄件網域已完成驗證，可以開始使用品牌網域寄送 Email。",
                    )}
                  </Typography>
                  <Box sx={styles.verifyOverlayActions}>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setShowSuccessModal(false);
                        onJumpToTab("email-info");
                      }}
                    >
                      {t("emailOnboard.dnsVerify.success.doneButton", "完成")}
                    </Button>
                    <Button
                      onClick={() => {
                        setShowSuccessModal(false);
                        onJumpToTab("email-info");
                        window.open(
                          `${import.meta.env["VITE_MAAC_URL"] || "https://maacstaging.cresclab.com"}/members/import`,
                          "_blank",
                          "noopener,noreferrer",
                        );
                      }}
                    >
                      {t(
                        "emailOnboard.dnsVerify.success.importButton",
                        "匯入 Email 聯絡人",
                      )}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          ) : null}
        </Box>

        {/* Verify button — placed below the records table so it sits right
            next to the data the user just reviewed (instead of floating
            top-right where it was easy to miss). Stays visible after
            verification as a "re-verify" affordance: DNS state can drift
            (TTL / cache / accidental removal), and hiding the action
            leaves the user with nowhere to go from this tab. */}
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant={allVerified ? "secondary" : "primary"}
            onClick={handleVerify}
            disabled={isVerifying}
            loading={isVerifying}
          >
            {allVerified
              ? t("emailOnboard.dnsVerify.reVerifyButton", "重新驗證")
              : t("emailOnboard.dnsVerify.verifyButton", "驗證 DNS")}
          </Button>
        </Stack>

        {/* Message for IT -- read-only content plus the two copy actions.
            Nothing here is editable: the operator edits in their own mail
            client after pasting, and the DNS values must reach IT exactly as
            the backend issued them. */}
        <Box>
          <Typography
            variant="h3"
            fontWeight={700}
            color={theme.colors.staticFgTitle}
            css={css({ marginBottom: 6 })}
          >
            {t("emailOnboard.dnsVerify.copyForIt.title", "DNS 設定通知")}
          </Typography>
          <Typography
            variant="note"
            color={theme.colors.staticFgNote}
            css={css({ marginBottom: 20 })}
          >
            {t(
              "emailOnboard.dnsVerify.copyForIt.hint",
              "已依上方記錄產生完整說明，並附常見設定注意事項。",
            )}
          </Typography>

          <Box component="pre" sx={styles.emailPreview}>
            {itEmailContent.plain}
          </Box>

          <Stack
            direction="row"
            justifyContent="flex-end"
            gap="8px"
            sx={{ mt: "14px" }}
          >
            <Button variant="secondary" onClick={handleCopyPlain}>
              {t(
                "emailOnboard.dnsVerify.copyForIt.copyPlainButton",
                "複製純文字",
              )}
            </Button>
            <Button onClick={handleCopyForEmail}>
              {t(
                "emailOnboard.dnsVerify.copyForIt.copyEmailButton",
                "複製 Email 格式",
              )}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </>
  );
};
